import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Flame, RotateCcw } from "lucide-react";
import { API, errMsg, fmtTime } from "../lib/api";
import { StatCard, SegmentedBar, PageHeader, EmptyState } from "../components/bits";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/dashboard").then(({ data }) => setData(data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const practiseIncorrect = async () => {
    try {
      const { data } = await API.post("/sessions", {
        topic: "all", count: 20, difficulty: "mixed", status_filter: "incorrect", mode: "quiz",
      });
      sessionStorage.setItem(`pamind_session_${data.session_id}`, JSON.stringify(data));
      navigate(`/app/session/${data.session_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground text-sm" data-testid="dashboard-loading">Loading your progress…</div>;
  if (!data) return null;
  const o = data.overall;

  return (
    <div data-testid="dashboard-page" className="max-w-6xl">
      <PageHeader title="Your revision at a glance" sub="Track progress, spot weak topics and start your next session.">
        <Button data-testid="start-session-btn" onClick={() => navigate("/app/topics")} className="px-6">
          Start a Question Session <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Questions attempted" value={o.attempted} testid="stat-attempted" />
        <StatCard label="Accuracy" value={o.accuracy === null ? "—" : `${o.accuracy}%`} sub={o.attempted ? `${o.correct} correct · ${o.incorrect} incorrect` : "No attempts yet"} testid="stat-accuracy" />
        <StatCard label="This week" value={o.this_week} sub={`${o.today} today`} testid="stat-week" />
        <StatCard label="Current streak" value={<span className="flex items-center gap-2">{o.streak} <Flame className="w-5 h-5 text-accent" /></span>} sub={o.streak === 1 ? "day" : "days"} testid="stat-streak" />
      </div>

      {o.incorrect > 0 && (
        <div className="mt-6 bg-primary text-primary-foreground rounded-lg p-6 flex flex-wrap items-center justify-between gap-4" data-testid="incorrect-cta">
          <div>
            <h3 className="font-display font-bold">You have {o.incorrect} incorrectly answered {o.incorrect === 1 ? "question" : "questions"}</h3>
            <p className="text-sm text-primary-foreground/70 mt-1">Your fastest gains come from fixing mistakes.</p>
          </div>
          <Button variant="secondary" data-testid="practise-incorrect-btn" onClick={practiseIncorrect}>
            <RotateCcw className="w-4 h-4 mr-2" /> Practise my incorrect questions
          </Button>
        </div>
      )}

      <h2 className="mt-10 mb-5 font-display text-xl font-bold" data-testid="topic-performance-title">Topic performance</h2>
      <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="topic-performance-list">
        {data.topics.map((t) => (
          <button key={t.slug} data-testid={`topic-row-${t.slug}`} onClick={() => navigate(`/app/topics/${t.slug}`)}
            className="w-full text-left px-5 py-4 hover:bg-muted/50 transition-colors grid md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-4">
              <p className="font-medium text-sm">{t.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t.attempted} / {t.published} attempted
                {t.attempted > 0 && ` · ${Math.round((t.correct / t.attempted) * 100)}% accuracy`}
              </p>
            </div>
            <div className="md:col-span-8">
              <SegmentedBar correct={t.correct} incorrect={t.incorrect} total={t.published} />
            </div>
          </button>
        ))}
      </div>
      <div className="mt-3 flex gap-5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-success inline-block" /> Correct</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-destructive inline-block" /> Incorrect</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-muted border border-border inline-block" /> Unanswered</span>
      </div>

      <h2 className="mt-10 mb-5 font-display text-xl font-bold">Recent sessions</h2>
      {data.recent_sessions.length === 0 ? (
        <EmptyState title="No sessions yet" body="Start your first question session to begin building your progress history." testid="no-sessions-state">
          <Button data-testid="empty-start-btn" onClick={() => navigate("/app/topics")}>Choose a topic</Button>
        </EmptyState>
      ) : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="recent-sessions-list">
          {data.recent_sessions.map((s) => (
            <button key={s.session_id} data-testid={`recent-session-${s.session_id}`}
              onClick={() => navigate(`/app/session/${s.session_id}/results`)}
              className="w-full text-left px-5 py-4 hover:bg-muted/50 transition-colors flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium text-sm capitalize">{s.mode} mode · {s.config?.topic === "all" ? "All topics" : s.config?.topic}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.score ? `${s.score.correct}/${s.score.answered} correct` : ""}</p>
              </div>
              <span className={`font-display font-bold text-lg ${s.score && s.score.answered ? (s.score.correct / s.score.answered >= 0.7 ? "text-success" : "text-destructive") : ""}`}>
                {s.score && s.score.answered ? `${Math.round((s.score.correct / s.score.answered) * 100)}%` : "—"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
